import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Key, Globe, Database, Users, FileText, CheckCircle, AlertTriangle } from 'lucide-react';

const Security = () => {
  return (
    <div className="security-page">
      {/* Hero Section */}
      <section className="security-hero">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="security-badge">
              <Shield className="h-8 w-8 text-green-500" />
              <span>Enterprise-Grade Security</span>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Your Privacy is Our Priority
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand your concerns about data security. That's why we've built PersonalityAI with 
              military-grade encryption, zero data sharing, and industry-leading security practices.
            </p>
          </div>
        </div>
      </section>

      {/* Security Overview */}
      <section className="security-overview py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Encrypted at Rest</h3>
              <p className="text-gray-600">All sensitive data is encrypted with AES-256-GCM before storage</p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Encrypted in Transit</h3>
              <p className="text-gray-600">TLS 1.3 encryption for all connections and API calls</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">User Isolation</h3>
              <p className="text-gray-600">Complete data separation - users can never see each other's data</p>
            </div>
          </div>
        </div>
      </section>

      {/* Encryption Details */}
      <section className="encryption-details py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">🔐 Military-Grade Encryption</h2>
            <p className="text-xl text-gray-600">How we protect your most sensitive information</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-semibold mb-6">AES-256-GCM Encryption</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">256-bit Key Strength</h4>
                    <p className="text-gray-600">Uses the same encryption standard as government agencies and banks</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Unique IV per Record</h4>
                    <p className="text-gray-600">Each piece of data gets a unique encryption vector for maximum security</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Authenticated Encryption</h4>
                    <p className="text-gray-600">Prevents tampering and ensures data integrity</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Automatic Key Rotation</h4>
                    <p className="text-gray-600">Encryption keys are automatically managed and rotated</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-900 text-white p-8 rounded-lg">
              <h4 className="text-lg font-semibold mb-4">What Gets Encrypted</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                  <span>Conversation content</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                  <span>Personal insights</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                  <span>Behavioral patterns</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                  <span>URLs and context</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                  <span>Session metadata</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Database Security */}
      <section className="database-security py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">🛡️ Database Security</h2>
            <p className="text-xl text-gray-600">Built on Supabase with enterprise-grade security</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Database className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">Row Level Security</h3>
              <p className="text-gray-600 text-sm">Users can only access their own data, even at the database level</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Key className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">JWT Authentication</h3>
              <p className="text-gray-600 text-sm">Secure token-based authentication with automatic expiration</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Eye className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="font-semibold mb-2">Access Logging</h3>
              <p className="text-gray-600 text-sm">Complete audit trail of all database access and modifications</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="font-semibold mb-2">Automatic Backups</h3>
              <p className="text-gray-600 text-sm">Daily encrypted backups with point-in-time recovery</p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Features */}
      <section className="privacy-features py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">🔒 Privacy by Design</h2>
            <p className="text-xl text-gray-600">Built from the ground up to protect your privacy</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-semibold mb-6">Zero Data Sharing</h3>
              <p className="text-gray-600 mb-6">
                Your data is never shared with third parties, advertisers, or other users. 
                Each user's data is completely isolated and encrypted.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>No data sold to advertisers</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>No data shared with third parties</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Complete user isolation</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Local processing when possible</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold mb-6">Data Control</h3>
              <p className="text-gray-600 mb-6">
                You have complete control over your data. Export it, delete it, or modify it at any time.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Export all your data anytime</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Delete your data completely</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Modify privacy settings</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span>Opt out of analytics</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="compliance py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">📋 Compliance & Standards</h2>
            <p className="text-xl text-gray-600">Meeting industry security and privacy standards</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200 text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🇪🇺</span>
              </div>
              <h3 className="font-semibold mb-2">GDPR Ready</h3>
              <p className="text-gray-600 text-sm">Right to be forgotten, data portability, and consent management</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200 text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🇺🇸</span>
              </div>
              <h3 className="font-semibold mb-2">CCPA Compliant</h3>
              <p className="text-gray-600 text-sm">California Consumer Privacy Act compliance</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200 text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🏢</span>
              </div>
              <h3 className="font-semibold mb-2">SOC 2 Ready</h3>
              <p className="text-gray-600 text-sm">Security, availability, and confidentiality standards</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200 text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🏥</span>
              </div>
              <h3 className="font-semibold mb-2">HIPAA Compatible</h3>
              <p className="text-gray-600 text-sm">Healthcare data protection standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* Security FAQ */}
      <section className="security-faq py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">❓ Security FAQ</h2>
            <p className="text-xl text-gray-600">Common questions about our security practices</p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-semibold mb-2">Can you read my conversations?</h3>
              <p className="text-gray-600">
                No. All conversation content is encrypted with AES-256-GCM encryption before storage. 
                Even if someone accessed our database, they couldn't read your content without the encryption keys.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-semibold mb-2">Is my data shared with other users?</h3>
              <p className="text-gray-600">
                Never. Each user's data is completely isolated using Row Level Security (RLS). 
                Users can only access their own data, even at the database level.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-semibold mb-2">What happens if you get hacked?</h3>
              <p className="text-gray-600">
                Even if our systems were compromised, your data would remain secure because it's encrypted. 
                The encryption keys are stored separately and securely managed.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-semibold mb-2">Can I delete my data?</h3>
              <p className="text-gray-600">
                Yes, absolutely. You can export all your data, delete specific records, or completely 
                delete your account and all associated data at any time.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-semibold mb-2">How do you handle data breaches?</h3>
              <p className="text-gray-600">
                We have a comprehensive incident response plan. In the unlikely event of a breach, 
                we would immediately notify affected users and take steps to secure the system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="security-cta py-16 bg-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to experience secure AI?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of users who trust us with their most sensitive conversations
          </p>
          <div className="space-x-4">
            <Link to="/register" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Get Started
            </Link>
            <Link to="/" className="text-white border border-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Security;
